/**
 * Combo chain badge — shows the active combo depth + multiplier.
 *
 * Subscribes to the ComboTracker. While idle (chain 0), the badge is
 * hidden. On the first merge it fades in; each subsequent link inside
 * the chain window pulses and updates the multiplier text. When the
 * chain expires, the badge fades out.
 */

import type { ComboTracker } from "../game/combo.ts";
import { t } from "../i18n/index.ts";

export interface ComboBadge {
  element: HTMLDivElement;
  /** Per-frame tick — drives the timer bar drain. */
  tick(nowMs: number): void;
  detach(): void;
}

export function attachComboBadge(
  tracker: ComboTracker,
  host: HTMLElement,
): ComboBadge {
  const element = document.createElement("div");
  element.dataset["testid"] = "combo-badge";
  element.className =
    "pointer-events-none absolute left-1/2 top-12 -translate-x-1/2 flex flex-col items-center gap-1 transition-opacity duration-150";
  element.style.opacity = "0";

  const text = document.createElement("div");
  text.dataset["testid"] = "combo-text";
  text.className =
    "rounded-full bg-cosmic-glow/85 px-4 py-1 font-display text-base text-cosmic-deep shadow-lg";

  const bar = document.createElement("div");
  bar.className = "h-1 w-32 overflow-hidden rounded-full bg-black/40";
  const fill = document.createElement("div");
  fill.dataset["testid"] = "combo-bar";
  fill.className = "h-full bg-cosmic-glow";
  fill.style.width = "100%";
  bar.appendChild(fill);

  element.append(text, bar);
  host.appendChild(element);

  let lastChain = 0;

  const unsub = tracker.subscribe((s) => {
    if (s.chain <= 0) {
      element.style.opacity = "0";
      lastChain = 0;
      return;
    }
    element.style.opacity = "1";
    text.textContent = t("combo.line", { n: s.multiplier, m: s.chain });
    if (s.chain !== lastChain) {
      // Pulse animation on each new link.
      element.style.transform = "translate(-50%, 0) scale(1.18)";
      window.setTimeout(() => {
        element.style.transform = "translate(-50%, 0) scale(1)";
      }, 120);
      lastChain = s.chain;
    }
  });

  const tick = (nowMs: number): void => {
    const s = tracker.state();
    if (s.chain <= 0) return;
    const remaining = s.expiresAt - nowMs;
    const window = s.expiresAt > 0 ? Math.max(remaining, 0) : 0;
    // Drain bar shows the remaining window. Default windowMs is 2000;
    // the tracker doesn't expose it, so derive from the most recent
    // merge by treating expiresAt as start + window. We approximate
    // with a 2000 cap which matches the default — sufficient for visual.
    const ratio = Math.min(1, Math.max(0, window / 2000));
    fill.style.width = `${ratio * 100}%`;
  };

  const detach = (): void => {
    unsub();
    element.remove();
  };

  return { element, tick, detach };
}
